/**
 * Shared UI kit for the consignment + tax profile admin pages.
 * Mirrors the building blocks in AdminTaxonomies.tsx (tenant-scope shell,
 * Panel, inline error, skeleton, inputs) so the two profile pages stay DRY
 * and consistent with the rest of the admin surface.
 */

import { Badge, Button, EmptyStateHelp, HelpTooltip, LearnMoreLink } from '@auctisync/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { readClaims, useSession } from '../../hooks/useSession';
import { supabase } from '../../lib/supabase';
import type { Tables } from '@auctisync/shared';

type TenantOption = Pick<Tables<'tenants'>, 'id' | 'slug' | 'name'>;

/** Resolve the tenant whose data this admin page manages (super admins pick). */
export function useTenantScope(): {
  tenantId: string;
  setTenantId: (id: string) => void;
  needsTenantPicker: boolean;
  tenantOptions: TenantOption[];
} {
  const { session } = useSession();
  const claims = readClaims(session);
  const [tenantOptions, setTenantOptions] = useState<TenantOption[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const needsTenantPicker = claims.isSuperAdmin && !claims.tenantId;
  // Derive the effective tenant from claims LIVE each render. The auth session
  // loads async (it is null on the first render), so seeding tenantId from a
  // mount-time useState froze it at '' and never recovered once the claim
  // arrived — the bug that stranded tenant admins (and super admins who also own
  // a tenant) on the "Pick a tenant" empty state. Only super admins with no
  // primary tenant fall back to the dropdown selection.
  const tenantId = needsTenantPicker ? selectedTenantId : (claims.tenantId ?? '');

  useEffect(() => {
    if (!needsTenantPicker) return;
    void (async () => {
      const { data } = await supabase
        .from('tenants')
        .select('id, slug, name')
        .eq('status', 'active')
        .order('name', { ascending: true });
      const list = data ?? [];
      setTenantOptions(list);
      const first = list[0];
      if (first && !selectedTenantId) setSelectedTenantId(first.id);
    })();
  }, [needsTenantPicker, selectedTenantId]);

  return { tenantId, setTenantId: setSelectedTenantId, needsTenantPicker, tenantOptions };
}

/** Full page shell: header + super-admin tenant picker + tenant-gated body. */
export function ProfileAdminPage({
  title,
  subtitle,
  helpSlug,
  learnMore,
  emptyDescription,
  children,
}: {
  title: string;
  subtitle: string;
  helpSlug: string;
  learnMore: string;
  emptyDescription: string;
  children: (tenantId: string) => ReactNode;
}) {
  const { tenantId, setTenantId, needsTenantPicker, tenantOptions } = useTenantScope();
  return (
    <div>
      <header style={{ marginBottom: '1.5rem' }}>
        <h1
          style={{
            fontFamily: 'var(--font-heading-active)',
            fontSize: '1.875rem',
            fontWeight: 700,
            color: 'var(--text)',
            marginBottom: '0.25rem',
          }}
        >
          {title}
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', marginBottom: '0.35rem' }}>
          {subtitle}
        </p>
        <LearnMoreLink helpSlug={helpSlug} newTab>
          {learnMore}
        </LearnMoreLink>
      </header>

      {needsTenantPicker && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            borderRadius: '0.375rem',
            background: 'var(--surface-muted, #fef3c7)',
            border: '1px solid var(--border)',
            fontSize: '0.875rem',
            flexWrap: 'wrap',
          }}
        >
          <Badge variant="outline">Super admin</Badge>
          <span>Managing profiles for tenant:</span>
          <select
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            style={selectStyle}
          >
            <option value="">— Pick a tenant —</option>
            {tenantOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.slug})
              </option>
            ))}
          </select>
        </div>
      )}

      {!tenantId ? (
        <EmptyStateHelp title="Pick a tenant" description={emptyDescription} helpSlug={helpSlug} />
      ) : (
        <div style={{ display: 'grid', gap: '1.5rem' }}>{children(tenantId)}</div>
      )}
    </div>
  );
}

export function Panel({
  title,
  subtitle,
  helpSlug,
  onAddClick,
  children,
}: {
  title: string;
  subtitle: string;
  helpSlug: string;
  onAddClick: () => void;
  children: ReactNode;
}) {
  return (
    <section
      style={{
        border: '1px solid var(--border)',
        borderRadius: '0.5rem',
        background: 'var(--surface-elev)',
        padding: '1rem 1.25rem',
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '0.75rem',
          marginBottom: '0.75rem',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h2
            style={{
              fontSize: '1rem',
              fontWeight: 600,
              color: 'var(--text)',
              marginBottom: '0.15rem',
            }}
          >
            {title} <HelpTooltip helpSlug={helpSlug}>Learn about this.</HelpTooltip>
          </h2>
          <p style={{ fontSize: '0.825rem', color: 'var(--text-muted)', margin: 0 }}>{subtitle}</p>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={onAddClick}>
          + Add
        </Button>
      </header>
      <div style={{ display: 'grid', gap: '0.5rem' }}>{children}</div>
    </section>
  );
}

export function InlineError({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      style={{
        padding: '0.5rem 0.75rem',
        borderRadius: '0.375rem',
        background: 'var(--surface-danger, #fef2f2)',
        color: 'var(--text-danger, #991b1b)',
        border: '1px solid var(--border-danger, #fecaca)',
        fontSize: '0.85rem',
      }}
    >
      {children}
    </div>
  );
}

export function SkeletonList() {
  return (
    <ul style={listStyle}>
      {[0, 1, 2].map((i) => (
        <li
          key={i}
          style={{
            height: '2.25rem',
            borderRadius: '0.375rem',
            background: 'var(--surface-muted)',
          }}
        />
      ))}
    </ul>
  );
}

/** A small "$"-prefixed decimal input (flat amounts). */
export function DollarInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ position: 'relative' }}>
      <span style={affixStyle}>$</span>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inputStyle, paddingLeft: '1.4rem' }}
        placeholder="0.00"
      />
    </div>
  );
}

/** A small "%"-suffixed decimal input (rates / commission). */
export function PercentInput({
  value,
  onChange,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  ariaLabel?: string;
}) {
  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={ariaLabel}
        style={{ ...inputStyle, paddingRight: '1.5rem' }}
        placeholder="0.00"
      />
      <span style={{ ...affixStyle, left: 'auto', right: '0.6rem' }}>%</span>
    </div>
  );
}

const affixStyle: React.CSSProperties = {
  position: 'absolute',
  left: '0.6rem',
  top: '50%',
  transform: 'translateY(-50%)',
  color: 'var(--text-muted)',
  fontSize: '0.875rem',
  pointerEvents: 'none',
};

export const listStyle: React.CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'grid',
  gap: '0.35rem',
};

export const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.45rem 0.65rem',
  border: '1px solid var(--border)',
  borderRadius: '0.375rem',
  background: 'var(--surface)',
  color: 'var(--text)',
  fontSize: '0.875rem',
};

export const selectStyle: React.CSSProperties = {
  ...inputStyle,
  width: 'auto',
};

export const editorBoxStyle: React.CSSProperties = {
  padding: '0.75rem',
  border: '1px solid var(--border)',
  borderRadius: '0.5rem',
  background: 'var(--surface-muted)',
  display: 'grid',
  gap: '0.75rem',
};

export const readRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '0.6rem 0.75rem',
  border: '1px solid var(--border)',
  borderRadius: '0.375rem',
  background: 'var(--surface)',
  gap: '0.75rem',
  flexWrap: 'wrap',
};
